import mixpanel from "mixpanel-browser";
import { auth } from "../../api/firebaseApi";
import { store } from "../../redux/store";

const MIXPANEL_IDENTIFIED_KEY = Symbol.for("cb.mixpanel.identified");
export const MIXPANEL_EVENTS = {
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGOUT: "LOGOUT",
  CASE_CREATED: "CASE_CREATED"
} as const;

export type MixpanelEventName =
  (typeof MIXPANEL_EVENTS)[keyof typeof MIXPANEL_EVENTS];

export const MIXPANEL_USER_TYPES = {
  LAWYER: "LAWYER",
  EMPLOYER_USER: "EMPLOYER_USER",
  INDIVIDUAL: "INDIVIDUAL",
  EXTERNAL_API: "EXTERNAL_API"
} as const;

export type MixpanelUserType =
  (typeof MIXPANEL_USER_TYPES)[keyof typeof MIXPANEL_USER_TYPES];

function inferMixpanelUserType(): MixpanelUserType | undefined {
  if (typeof window === "undefined") return undefined;
  const loginType = window.localStorage?.getItem("loginType") ?? "";
  if (loginType === "lawyer") return MIXPANEL_USER_TYPES.LAWYER;
  if (loginType === "individual") return MIXPANEL_USER_TYPES.INDIVIDUAL;
  return undefined;
}

function getUserProperties(): Record<string, unknown> {
  const user = auth.currentUser;
  const userType = inferMixpanelUserType();

  const state = store.getState() as unknown as {
    lawyer?: { practiceName?: string; email?: string };
  };

  const props: Record<string, unknown> = {};
  if (userType) props.user_type = userType;

  const email =
    user?.email ||
    (userType === MIXPANEL_USER_TYPES.LAWYER ? state.lawyer?.email : undefined);
  if (email) props.user_email = email;

  if (userType === MIXPANEL_USER_TYPES.LAWYER) {
    const practiceName = state.lawyer?.practiceName;
    if (practiceName) props.practice_name = practiceName;
  }

  return props;
}

function isMixpanelInitialized(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const mp = mixpanel as unknown as {
      track?: (...args: unknown[]) => unknown;
      identify?: (...args: unknown[]) => unknown;
      people?: { set?: (...args: unknown[]) => unknown };
      get_config?: (key: string) => unknown;
    };

    if (typeof mp.track !== "function") return false;
    // mixpanel-browser sets a token via init(); if missing, treat as not ready.
    const token =
      typeof mp.get_config === "function" ? mp.get_config("token") : undefined;
    return typeof token === "string" && token.length > 0;
  } catch {
    return false;
  }
}

function ensureIdentified() {
  if (typeof window === "undefined") return;
  if (!isMixpanelInitialized()) return;
  const w = window as any;
  if (w[MIXPANEL_IDENTIFIED_KEY]) return;

  try {
    const user = auth.currentUser;
    if (user) {
      mixpanel.identify(user.uid);
      if (user.email) {
        mixpanel.people.set("$email", user.email);
      }
      w[MIXPANEL_IDENTIFIED_KEY] = true;
    }
  } catch {
    // Analytics should never break product flows.
  }
}

export function track(
  event: MixpanelEventName,
  properties?: Record<string, unknown>,
  options?: { userType?: MixpanelUserType }
) {
  if (typeof window === "undefined") return;
  if (!isMixpanelInitialized()) return;
  try {
    ensureIdentified();
    const enriched: Record<string, unknown> = {
      timestamp: Date.now(),
      ...getUserProperties(),
      ...(options?.userType ? { user_type: options.userType } : {}),
      ...(properties ?? {})
    };
    mixpanel.track(event, enriched);
  } catch {
    // Analytics should never break product flows.
  }
}

export function identifyMixpanelUser(uid: string, email?: string) {
  if (typeof window === "undefined") return;
  if (!isMixpanelInitialized()) return;
  try {
    mixpanel.identify(uid);
    if (email) {
      mixpanel.people.set("$email", email);
    }
    const w = window as unknown as Record<symbol, unknown>;
    w[MIXPANEL_IDENTIFIED_KEY] = true;
  } catch {
    // Analytics should never break product flows.
  }
}

export function resetMixpanel() {
  if (typeof window === "undefined") return;
  if (!isMixpanelInitialized()) return;
  try {
    mixpanel.reset();
    const w = window as unknown as Record<symbol, unknown>;
    delete w[MIXPANEL_IDENTIFIED_KEY];
  } catch {
    // Analytics should never break product flows.
  }
}
